import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Button, TextField } from "@mui/material";
import { useDebouncedValue } from "hooks/useDebouncedValue";
import { moneyAud, formatDate } from "../../utils/portalHelpers";
import { Panel, EmptyState, LoadingPanel, ErrorPanel, StatusChip } from "../../components/ui";
import { partnerQueryKeys } from "../../api/queryKeys";
import { fetchPartnerClients } from "../../api/endpoints/workspace";
import usePartnerQuery from "../../hooks/usePartnerQuery";
import LeadsPanel from "../Leads/LeadsPanel";
import LeadEditorDrawer from "../Leads/LeadEditorDrawer";

const PAGE_SIZE = 20;

export const CLIENTS_PAGE_TABS = [
  { id: "clients", label: "Clients" },
  { id: "leads", label: "Leads" },
];

const ClientsList = () => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);

  // Free text fires a request per keystroke without this; the input stays instant while the
  // query waits for a pause.
  const debouncedSearch = useDebouncedValue(search.trim(), 400);

  const params = useMemo(
    () => ({ page, size: PAGE_SIZE, ...(debouncedSearch ? { search: debouncedSearch } : {}) }),
    [page, debouncedSearch]
  );

  const { data, isLoading, isError, refetch } = usePartnerQuery(
    partnerQueryKeys.clients(params),
    () => fetchPartnerClients(params),
    { keepPreviousData: true }
  );

  const rows = data?.content || [];
  const totalPages = data?.totalPages ?? 0;

  return (
    <div>
      <div className="mb-4 max-w-sm">
        <TextField
          fullWidth
          size="small"
          label="Search name, email or phone"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(0);
          }}
        />
      </div>

      {isLoading ? (
        <LoadingPanel label="Loading your clients…" />
      ) : isError ? (
        <ErrorPanel
          description="The server did not answer. Nothing is lost, try again."
          action={
            <button
              type="button"
              onClick={() => refetch()}
              className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white"
            >
              Try again
            </button>
          }
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title={search ? "No clients match that search" : "No clients yet"}
          description={
            search
              ? "Try a different name, email or phone number."
              : "A referral becomes a client here once the office activates it."
          }
        />
      ) : (
        <Panel title={`${data?.totalItems ?? rows.length} client${rows.length === 1 ? "" : "s"}`}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
                  <th className="py-2 pr-3">Name</th>
                  <th className="py-2 pr-3">Contact</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Since</th>
                  <th className="py-2 pr-3 text-right">Your commission</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((client) => (
                  <tr key={client.clientId} className="border-t border-slate-100">
                    <td className="py-2.5 pr-3">
                      <Link
                        to={`/partner/clients/${client.clientId}`}
                        className="font-medium text-teal-700 no-underline"
                      >
                        {client.name}
                      </Link>
                    </td>
                    <td className="py-2.5 pr-3 text-slate-600">
                      <div>{client.email || "—"}</div>
                      <div className="text-xs text-slate-500">{client.phone || "—"}</div>
                    </td>
                    <td className="py-2.5 pr-3">
                      <StatusChip tone={client.status === "ACTIVE" ? "emerald" : "neutral"}>
                        {client.status === "ACTIVE" ? "Active" : "Inactive"}
                      </StatusChip>
                    </td>
                    <td className="py-2.5 pr-3 text-slate-600">{formatDate(client.createdAt)}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums">
                      {moneyAud(client.myCommission)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 ? (
            <div className="flex items-center justify-between gap-3 pt-3 mt-3 border-t border-slate-100">
              <button
                type="button"
                disabled={page === 0}
                onClick={() => setPage((current) => Math.max(current - 1, 0))}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold disabled:opacity-40"
              >
                Previous
              </button>
              <span className="text-xs text-slate-500">
                Page {page + 1} of {totalPages}
              </span>
              <button
                type="button"
                disabled={page + 1 >= totalPages}
                onClick={() => setPage((current) => current + 1)}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold disabled:opacity-40"
              >
                Next
              </button>
            </div>
          ) : null}
        </Panel>
      )}
    </div>
  );
};

/**
 * Clients and leads are the same people at different stages, so they share one page: the
 * Leads tab holds referrals the office has not activated yet, Clients holds the activated ones.
 */
const ClientsPage = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "leads" ? "leads" : "clients";
  const [editor, setEditor] = useState({ open: false, lead: null });

  const selectTab = (id) => setSearchParams(id === "clients" ? {} : { tab: id }, { replace: true });

  const openLead = (lead = null) => setEditor({ open: true, lead });

  return (
    <div>
      <div className="mb-5 flex items-end justify-between gap-3 border-b border-slate-200">
        <div role="tablist" aria-label="Clients and leads" className="flex gap-6">
          {CLIENTS_PAGE_TABS.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={tab === item.id}
              onClick={() => selectTab(item.id)}
              className={[
                "-mb-px border-0 border-b-2 bg-transparent px-1 pb-2.5 text-sm font-semibold cursor-pointer",
                tab === item.id
                  ? "border-teal-600 text-teal-800"
                  : "border-transparent text-slate-500 hover:text-slate-800",
              ].join(" ")}
            >
              {item.label}
            </button>
          ))}
        </div>
        <div className="pb-2">
          <Button
            variant="contained"
            size="small"
            disableElevation
            sx={{ bgcolor: "#0f766e" }}
            onClick={() => openLead()}
          >
            New client
          </Button>
        </div>
      </div>

      {tab === "leads" ? (
        <LeadsPanel onNewLead={() => openLead()} onEditLead={openLead} />
      ) : (
        <ClientsList />
      )}

      <LeadEditorDrawer
        open={editor.open}
        lead={editor.lead}
        onClose={() => setEditor({ open: false, lead: null })}
        onSaved={() => {
          queryClient.invalidateQueries(partnerQueryKeys.leads());
          // A lead saved from the Clients tab lands in Leads, so show it there.
          if (!editor.lead) selectTab("leads");
        }}
      />
    </div>
  );
};

export default ClientsPage;
